/**
 * Bottom navigation.
 *
 * The tab bar is the most persistent statement the product makes about what it
 * is about, so what it *lists* matters more than how it is styled. It used to
 * read Today / Progress / Move / Run / Food: three of five permanent
 * destinations were activity and intake, two of those were exercise
 * modalities, and the thing this product is actually about — how the person
 * feels — had no destination at all. That is a fitness tracker's census.
 *
 * Two changes fix the census rather than the paint:
 *
 * 1. Run folded into Move. A run is a kind of movement, and Move already
 *    carries runs, run logging and run programmes — the separate slot was a
 *    duplicate surface spending permanent chrome on a second gym modality. No
 *    reference wellness app gives two exercise modalities top-level slots.
 * 2. "Progress" became "Looking back". Progress is a performance word, it was
 *    the second-most-read word in the product, and it is the tab you least
 *    want to open on a flat week — the exact loss-aversion framing this
 *    product rules out. The tab holds mood evidence and the long view: a
 *    record, not a scoreboard. Oura says Trends; nobody calm says Progress.
 *
 * What is left is four spacious destinations, half of them the emotional
 * layer: how today is, how the days have been, what you might do, and — last
 * and quiet, because a calorie surface has no business holding billing in
 * permanent chrome — food.
 *
 * Nothing here is allowed to read as gym equipment: no dumbbell, no sprinter,
 * no cutlery. The day opens on a sunrise, the record is a page of days,
 * movement is a person walking, food is a plain bowl.
 *
 * All four glyphs are drawn on one optical grid — a 24 unit box with the ink
 * kept inside roughly x 3.5–20.5 and y 4.5–19.5 — and they carry a similar
 * *amount* of ink rather than a similar bounding box, so no mark reads lighter
 * than its neighbours when the row is greyscaled (roughly 50–57 square units
 * each). Only one glyph is a human figure; a second would scan as the same
 * mark at 22px.
 *
 * The active destination is never signalled by hue alone: a filled dot appears
 * beneath the current tab and its label steps to medium, both of which survive
 * greyscale and colour-blind vision (WCAG 1.4.1). See `.shell-nav-item::after`.
 *
 * These are destinations, not tabs in a tabpanel sense, so the bar is a plain
 * <nav> with aria-current="page" rather than a tablist that promises arrow-key
 * behaviour it does not implement.
 */
import { PATIENT_TABS } from "../lib/product";

/**
 * Which destinations get a slot, and in what order, is decided in
 * src/lib/product.js and nowhere else.
 */
const ALL_NAV_ITEMS = [
  {
    id: "home",
    label: "Today",
    // Sunrise over a horizon: the day, opening.
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3.6 19h16.8" />
        <path d="M7.4 19a4.6 4.6 0 0 1 9.2 0" />
        <path d="M12 6.7v2.4" />
        <path d="m5.9 9.6 1.7 1.7" />
        <path d="m18.1 9.6-1.7 1.7" />
      </svg>
    ),
  },
  {
    id: "profile",
    label: "Looking back",
    // A page of days: a rail with two rows of day-dots under it. This slot
    // used to hold a single open arc, which is the universal loading-spinner
    // mark — at 22px it read as "this tab is loading", not as a record. A
    // closed form with internal structure cannot make that mistake, and the
    // rail is what keeps it from reading as the generic "apps" grid.
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.6 6.8h14.8" />
        <circle cx="7.6" cy="12" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="16.4" cy="12" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="7.6" cy="16.8" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="12" cy="16.8" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="16.4" cy="16.8" r="1.35" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    id: "train",
    label: "Move",
    // A person walking, upright and unhurried — not a rep, not a machine.
    // Runs live here too now.
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="13.2" cy="6.8" r="2.4" />
        <path d="M12.9 10.4 11.1 14.4 7.4 19.2" />
        <path d="M11.1 14.4 15.1 15.7 16.6 19.2" />
        <path d="M12.4 11.6 8.8 13" />
        <path d="M12.4 11.6 16.6 12.8" />
      </svg>
    ),
  },
  {
    id: "nutrition",
    label: "Food",
    // A plain bowl, and a bowl of steam. Food is nourishment here, not a
    // measurement — and it sits last on purpose.
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3.9 12.4h16.2" />
        <path d="M5.4 12.4a6.6 6.6 0 0 0 13.2 0" />
        <path d="M9.4 9.2q2.1-2.5 4.2 0" />
      </svg>
    ),
  },
];

// Ordered by PATIENT_TABS, so the product decides the order in one place:
// Today, Move, Looking back, Food.
const NAV_ITEMS = PATIENT_TABS.map((id) => ALL_NAV_ITEMS.find((i) => i.id === id)).filter(Boolean);

export default function BottomNav({ tab, setTab }) {
  return (
    <nav className="shell-nav" aria-label="Main navigation">
      {NAV_ITEMS.map((n) => (
        <button
          key={n.id}
          type="button"
          aria-current={tab === n.id ? "page" : undefined}
          className="shell-nav-item"
          onClick={() => setTab(n.id)}
        >
          <span className="shell-nav-glyph" aria-hidden="true">{n.icon}</span>
          <span className="shell-nav-label">{n.label}</span>
        </button>
      ))}
    </nav>
  );
}
